// Bot Call Service
// Uses Twilio Voice API to make automated phone calls to businesses
// Handles appointment requests when SMS is not available or no response received

import { appointmentRequestService, AppointmentRequest } from './appointmentRequestService';

export interface BotCallResult {
  success: boolean;
  callSid?: string;
  status?: 'queued' | 'ringing' | 'in-progress' | 'completed' | 'busy' | 'failed' | 'no-answer';
  error?: string;
  response?: 'confirmed' | 'declined' | 'will_call' | 'reschedule' | 'no_response';
  suggestedTime?: string;
}

import { TWILIO_CONFIG } from '../config/twilioConfig';

class BotCallService {
  // Use centralized Twilio config (handles web vs mobile automatically)
  private readonly TWILIO_ACCOUNT_SID = TWILIO_CONFIG.accountSid;
  private readonly TWILIO_AUTH_TOKEN = TWILIO_CONFIG.authToken;
  private readonly TWILIO_PHONE_NUMBER = TWILIO_CONFIG.phoneNumber;
  private readonly APP_URL = process.env.APP_URL || 'https://bookapt.io';
  // Use the Render webhook URL for voice calls
  private readonly WEBHOOK_URL = process.env.TWILIO_VOICE_WEBHOOK || 'https://bookapt-sms-webhook.onrender.com/api/twilio/voice-handler';

  /**
   * Check if bot calls are enabled
   */
  private isEnabled(): boolean {
    return !!(this.TWILIO_ACCOUNT_SID && this.TWILIO_AUTH_TOKEN && this.TWILIO_PHONE_NUMBER);
  }

  /**
   * Check if it's business hours (9 AM - 6 PM local time)
   */
  private isBusinessHours(timezone: string = 'America/New_York'): boolean {
    const now = new Date();
    const localTime = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const hour = localTime.getHours();
    return hour >= 9 && hour < 18; // 9 AM to 6 PM
  }

  /**
   * Make automated call to business about appointment request
   */
  async makeAppointmentCall(
    request: AppointmentRequest,
    reason: 'landline' | 'no_sms_response' | 'escalation' = 'no_sms_response'
  ): Promise<BotCallResult> {
    try {
      if (!this.isEnabled()) {
        console.warn('⚠️ Bot calls not enabled - Twilio credentials missing');
        return {
          success: false,
          error: 'Bot calls not configured',
          status: 'failed',
        };
      }

      // Check business hours - only call during business hours
      if (!this.isBusinessHours()) {
        console.log('⏰ Outside business hours - will queue call for next business day');
        // In production, queue the call
        return {
          success: false,
          error: 'Outside business hours',
          status: 'queued',
        };
      }

      console.log(`📞 Making bot call to ${request.businessName} at ${request.businessPhone}`);
      console.log(`📋 Reason: ${reason}`);

      // In development, simulate the call
      if (__DEV__) {
        console.log('🔧 [DEV MODE] Simulating bot call...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        return {
          success: true,
          callSid: `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          status: 'completed',
          response: 'no_response', // Simulate no response for testing
        };
      }

      // Production: Make real call via Twilio Voice API
      const callUrl = `${this.WEBHOOK_URL}?requestId=${request.id}&reason=${reason}`;
      
      const response = await fetch(
        `https://api.twilio.com/2010-04-01/Accounts/${this.TWILIO_ACCOUNT_SID}/Calls.json`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Basic ${btoa(`${this.TWILIO_ACCOUNT_SID}:${this.TWILIO_AUTH_TOKEN}`)}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            To: request.businessPhone,
            From: this.TWILIO_PHONE_NUMBER,
            Url: callUrl,
            Method: 'POST',
            StatusCallback: `${this.WEBHOOK_URL}/status?requestId=${request.id}`,
            StatusCallbackMethod: 'POST',
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        console.log('✅ Bot call initiated:', data.sid);
        return {
          success: true,
          callSid: data.sid,
          status: data.status as any,
        };
      } else {
        console.error('❌ Bot call failed:', data);
        return {
          success: false,
          error: data.message || 'Failed to initiate call',
          status: 'failed',
        };
      }
    } catch (error: any) {
      console.error('❌ Bot call error:', error);
      return {
        success: false,
        error: error.message || 'Network error making call',
        status: 'failed',
      };
    }
  }

  /**
   * Generate TwiML for appointment request call
   * This is what gets played when business answers
   */
  generateCallTwiML(request: AppointmentRequest): string {
    const formattedDate = this.formatDate(request.preferredDate);
    
    // TwiML XML for voice call
    return `<?xml version="1.0" encoding="UTF-8"?>
<Response>
  <Say voice="alice">
    Hello, this is BookAPT calling about an appointment request for ${request.businessName}.
  </Say>
  <Pause length="1"/>
  <Say voice="alice">
    You have a new appointment request from customer ${request.userName}.
  </Say>
  <Pause length="1"/>
  <Say voice="alice">
    Service: ${request.service}.
  </Say>
  <Say voice="alice">
    Date: ${formattedDate}.
  </Say>
  <Say voice="alice">
    Time: ${request.preferredTime}.
  </Say>
  ${request.specialRequests ? `<Say voice="alice">Notes: ${request.specialRequests}.</Say>` : ''}
  <Pause length="1"/>
  <Say voice="alice">
    To confirm this appointment, press 1. To decline, press 2. To speak with the customer, press 3. To reschedule, press 4.
  </Say>
  <Gather numDigits="1" action="${this.WEBHOOK_URL}/process?requestId=${request.id}" method="POST" timeout="10">
    <Say voice="alice">Please press a number now.</Say>
  </Gather>
  <Say voice="alice">
    We didn't receive a response. We'll send you a text message with the details. Thank you!
  </Say>
  <Hangup/>
</Response>`;
  }

  /**
   * Process DTMF response from call
   */
  async processCallResponse(
    requestId: string,
    digit: string
  ): Promise<BotCallResult> {
    try {
      const request = await appointmentRequestService.getRequest(requestId);
      if (!request) {
        return {
          success: false,
          error: 'Request not found',
          status: 'failed',
        };
      }

      let response: 'confirmed' | 'declined' | 'will_call' | 'reschedule' | 'no_response' = 'no_response';
      let suggestedTime: string | undefined;

      switch (digit) {
        case '1':
          // Confirm appointment
          response = 'confirmed';
          await appointmentRequestService.processBusinessResponse(requestId, 'YES');
          break;
        case '2':
          // Decline appointment
          response = 'declined';
          await appointmentRequestService.processBusinessResponse(requestId, 'NO');
          break;
        case '3':
          // Business will call customer
          response = 'will_call';
          await appointmentRequestService.processBusinessResponse(requestId, 'CALL');
          break;
        case '4':
          // Reschedule - need to get time
          response = 'reschedule';
          // In production, use Gather to get time
          await appointmentRequestService.processBusinessResponse(requestId, 'RESCHEDULE');
          break;
        default:
          response = 'no_response';
      }

      return {
        success: true,
        status: 'completed',
        response,
        suggestedTime,
      };
    } catch (error: any) {
      console.error('Error processing call response:', error);
      return {
        success: false,
        error: error.message,
        status: 'failed',
      };
    }
  }

  /**
   * Generate TwiML for processing response
   */
  generateResponseTwiML(response: 'confirmed' | 'declined' | 'will_call' | 'reschedule'): string {
    let message = '';
    
    switch (response) {
      case 'confirmed':
        message = 'Thank you! Your appointment is confirmed. We will send you a confirmation text message.';
        break;
      case 'declined':
        message = 'Thank you for letting us know. We will notify the customer.';
        break;
      case 'will_call':
        message = 'Thank you! We will provide you with the customer contact information via text message.';
        break;
      case 'reschedule':
        message = 'Thank you! Please reply to our text message with your preferred time.';
        break;
    }

    return `<?xml version="1.0" encoding="UTF-8"?>
<Response>
  <Say voice="alice">${message}</Say>
  <Hangup/>
</Response>`;
  }

  /**
   * Format date for voice
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      return dateString;
    }
  }
}

export const botCallService = new BotCallService();
export default botCallService;






